import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import * as yup from "yup";
import {DateTime} from "luxon";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {formatFromISOToDate} from "../../../utils/dateTimeHelper";

export const useEnrollmentForm = (data) => {
    const {t} = useTranslation();

    const getMinSelectableDate = useCallback(() => {
        let date = DateTime.now().plus({days: 3}).startOf("day");

        if (date.weekday === 6) date = date.plus({days: 2});
        if (date.weekday === 7) date = date.plus({days: 1});

        return date;
    }, []);

    const defaultValues = useMemo(() => ({
        desiredStartDate: data?.desiredStartDate ? formatFromISOToDate(data?.desiredStartDate) : "",
        notionalYearGroupId: data?.notionalYearGroupId || "",
        performanceAreaId: data?.performanceAreaId || "",
    }), [data]);

    const schema = useMemo(() => yup.object().shape({
        desiredStartDate: yup.string()
            .required(t("validation.required"))
            .test("is-valid-date", t("validation.invalidDate"), (value) => {
                if (!value) return false;
                return DateTime.fromISO(value).isValid;
            })
            .test("is-weekday", t("validation.weekdaysOnly"), (value) => {
                if (!value) return false;
                const selectedDate = DateTime.fromISO(value);
                if (!selectedDate.isValid) return false;

                return selectedDate.weekday >= 1 && selectedDate.weekday <= 5;
            })
            .test("min-business-date", t("validation.minStartDate"), (value) => {
                if (!value) return false;
                const selectedDate = DateTime.fromISO(value).startOf("day");
                if (!selectedDate.isValid) return false;

                return selectedDate >= getMinSelectableDate();
            }),
        notionalYearGroupId: yup.string().required(t("validation.required")),
        performanceAreaId: yup.string().required(t("validation.required")),
    }), [t, getMinSelectableDate]);

    const {
        control,
        handleSubmit,
        formState: {errors, isValid, isDirty},
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues,
    });

    return {
        handleSubmit,
        control,
        errors,
        isValid,
        isDirty,
    };
};
